import { type CSSObject, styled, type Theme, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MuiListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { type ComponentType, type ReactElement } from 'react';
import SystemSecurityUpdateGoodIcon from '@mui/icons-material/SystemSecurityUpdateGood';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { Tooltip, useMediaQuery } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import HomeIcon from '@mui/icons-material/Home';
import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { closedDrawerWidth, openDrawerWidth, topBarHeight } from './constants';

interface LeftDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface PageItem {
  title: string;
  url: string;
  Icon: ComponentType<{ sx?: object }>;
}

export const LeftDrawer = ({ open, onClose }: LeftDrawerProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const pages: PageItem[] = [
    {
      title: t('nav.clients', 'Clients'),
      url: '/clients',
      Icon: HandshakeIcon,
    },
    {
      title: t('nav.appConfigurations', 'App Configurations'),
      url: '/app-configurations',
      Icon: SystemSecurityUpdateGoodIcon,
    },
  ];

  return (
    <Drawer variant="permanent" open={open} onClose={onClose}>
      <DrawerHeader sx={{ minHeight: 50 }}>
        {isMobile ? (
          <IconButton onClick={onClose}>
            <MenuIcon sx={{ ml: 0.5 }} />
          </IconButton>
        ) : (
          open && (
            <IconButton
              disableRipple
              onClick={onClose}
              sx={(theme) => ({
                ml: 'auto',
                ...theme.applyStyles('dark', {
                  color: 'grey.500',
                  '&:hover': { backgroundColor: 'grey.900' },
                }),
              })}
            >
              {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          )
        )}
      </DrawerHeader>
      <Box
        sx={{
          width: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          pt: 3,
          mb: 0,
        }}
      >
        <List>
          {pages.map((x) => (
            <ListItem key={x.title} Icon={x.Icon} open={open} url={x.url} title={x.title} />
          ))}
        </List>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
          <List sx={{ width: '100%' }}>
            <ListItem
              Icon={HomeIcon}
              open={open}
              url={'/'}
              title={t('nav.home', 'Home')}
              end
              iconSx={{ fontSize: theme.fontSizes.f30 }}
            />
          </List>
        </Box>
      </Box>
    </Drawer>
  );
};

interface ListItemProps {
  Icon: ComponentType<{ sx?: object }>;
  open: boolean;
  url: string;
  title: string;
  end?: boolean;
  iconSx?: object;
}

const ListItem = (props: ListItemProps) => {
  const { Icon } = props;
  return (
    <ListItemToolTip showToolTip={!props.open} label={props.title}>
      <MuiListItem
        disablePadding
        sx={{
          display: 'block',
        }}
      >
        <NavLink
          to={props.url}
          end={props.end}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          {({ isActive }) => (
            <ListItemButton
              disableRipple
              selected={isActive}
              sx={(theme) => ({
                minHeight: 48,
                px: 'calc(var(--closed-drawer-width) / 2 - 18px)',
                alignItems: 'center',
                justifyContent: 'initial',
                borderRadius: 1,
                mx: 1,
                [theme.breakpoints.down('sm')]: {
                  justifyContent: 'center',
                  px: 0,
                },
                '&:hover': {
                  '& .MuiSvgIcon-root': { color: 'primary.main' },
                },
                ...theme.applyStyles('dark', {
                  '&:hover': {
                    backgroundColor: 'background.default',
                    '& .MuiSvgIcon-root': { color: 'grey.50' },
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'grey.900',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'grey.900',
                    '& .MuiSvgIcon-root': { color: 'grey.50' },
                  },
                }),
              })}
            >
              <ListItemIcon
                sx={(theme) => ({
                  minWidth: 20,
                  width: 20,
                  justifyContent: 'center',
                  mr: props.open ? 2 : 0,
                  [theme.breakpoints.down('sm')]: {
                    mr: 0,
                  },
                })}
              >
                <Icon
                  sx={(theme: Theme) => ({
                    fontSize: theme.fontSizes.f20,
                    color: 'grey.500',
                    ...theme.applyStyles('dark', {
                      color: 'grey.600',
                    }),
                    ...props.iconSx,
                  })}
                />
              </ListItemIcon>
              <ListItemText
                primary={props.title}
                sx={(theme) => ({
                  display: props.open ? 'block' : 'none',
                  [theme.breakpoints.down('sm')]: {
                    display: 'none',
                  },
                })}
              />
            </ListItemButton>
          )}
        </NavLink>
      </MuiListItem>
    </ListItemToolTip>
  );
};

interface ListItemToolTipProps {
  children: ReactElement;
  label: string;
  showToolTip: boolean;
}

const ListItemToolTip = (props: ListItemToolTipProps) => {
  if (!props.showToolTip) {
    return props.children;
  }

  return (
    <Tooltip title={props.label} placement="right-end">
      {props.children}
    </Tooltip>
  );
};

const openedMixin = (theme: Theme): CSSObject => ({
  width: openDrawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  [theme.breakpoints.down('sm')]: {
    width: `${closedDrawerWidth}px`,
  },
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: 'var(--closed-drawer-width)',
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1),
  height: topBarHeight,
  flexShrink: 0,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => {
  const darkPaperStyles = theme.applyStyles('dark', {
    backgroundColor: theme.vars?.palette.shades.neutral,
    backdropFilter: 'blur(20px)',
  });

  return {
    width: 'var(--closed-drawer-width)',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    '& .MuiDrawer-paper': {
      borderRight: 'none',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: topBarHeight - 1,
        left: 0,
        right: 0,
        height: '1px',
        backgroundColor: theme.vars?.palette.divider,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: topBarHeight,
        right: 0,
        bottom: 0,
        width: '1px',
        backgroundColor: theme.vars?.palette.divider,
      },
      ...darkPaperStyles,
      ...theme.applyStyles('dark', {
        '&::before': {
          height: '0.5px',
          top: topBarHeight - 0.5,
        },
        '&::after': {
          width: '0.5px',
        },
      }),
    },
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': {
            ...openedMixin(theme),
            ...darkPaperStyles,
          },
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': {
            ...closedMixin(theme),
            ...darkPaperStyles,
          },
        },
      },
    ],
  };
});
